import React from "react";
import { useRouteMatch } from "react-router-dom";
import { useSelector } from "react-redux";

import { FeatureFlagEnum } from "@/features/feature-flag/constants";
import { FeatureGate } from "@/features/feature-flag/components/FeatureGate";
import { isUserPCP } from "@/utils/authToken";
import type { PatientDetails, UUID } from "@/core/types";
import type { RootState } from "@/lib/types/state";

import { AcuityUpdateList } from "../(care-plan-acuity)/AcuityUpdateList";
import { isCareTeamMember } from "../helpers/is-team-member";

import { A5Assessment } from "./components/A5Assessment/A5Assessment";
import { CarePlanScreenings } from "./components/CarePlanScreenings";
import { CareTeam } from "./components/CareTeam/CareTeam";
import { DemographicsCard } from "./components/DemographicCard";
import { GoalsAndObjectives } from "./components/GoalsAndObjectives/GoalsAndObjectives";
import { GoalsStrengthsCautions } from "./components/GoalsStrengthsCautions/GoalsStrengthsCautions";
import LegacyCarePlan from "./components/LegacyCarePlan/LegacyCarePlan";
import { MemberProfileCarePlanSkeleton } from "./components/MemberProfileCarePlanSkeleton";
import { useCarePlan } from "./api/queries/useCarePlan";
import { useCareTeam } from "./api/queries/useCareTeam";

import "./MemberProfileCarePlan.styles.scss";

type Props = {
  patientDetails: PatientDetails;
};

export function MemberProfileCarePlan({ patientDetails }: Props) {
  const match = useRouteMatch<{ patientId: UUID }>();

  const profile = useSelector((state: RootState) => state.auth.profile);
  const patientId = match.params.patientId;

  const { data: carePlan, isLoading } = useCarePlan({ patientId });
  const { data: careTeamMembers = [], refetch: refreshCareTeam } = useCareTeam({ patientId });

  const isProviderOnCareTeam = isCareTeamMember(profile, careTeamMembers);

  if (isLoading) {
    return <MemberProfileCarePlanSkeleton />;
  }

  return (
    <>
      <FeatureGate defaultFlagValue={false} flagName={FeatureFlagEnum.acuityOverride}>
        <AcuityUpdateList patientId={patientId} />
      </FeatureGate>

      <DemographicsCard
        demographics={{
          ...carePlan?.demographics,
          pcp_location: patientDetails?.pcp_location,
          pcp_name: patientDetails?.pcp_name,
        }}
      />

      <CareTeam careTeamMembers={careTeamMembers} patientId={patientId} />

      {/* Care plan doesn't exist for unregistered patients */}
      {carePlan?.care_plan_id && (
        <>
          <GoalsAndObjectives
            careTeamMembers={careTeamMembers}
            isProviderOnCareTeam={isProviderOnCareTeam}
            patientDetails={patientDetails}
            patientId={patientId}
            refreshCareTeam={refreshCareTeam}
          />

          <GoalsStrengthsCautions
            isProviderOnCareTeam={isProviderOnCareTeam}
            patientId={patientId}
          />
        </>
      )}

      {!isUserPCP() && <A5Assessment assessmentSummaries={carePlan?.assessment_summaries} />}

      <CarePlanScreenings
        gadSummary={carePlan?.assessment_summaries?.gad}
        phqSummary={carePlan?.assessment_summaries?.phq}
      />

      <LegacyCarePlan
        isPatientInactive={patientDetails?.status === "INACTIVE"}
        patientId={patientId}
      />
    </>
  );
}
